import { useEffect, useRef, useState } from 'react';

/**
 * Scroll-driven 3D "exploded stack": the FastWhistle platform starts as one closed block
 * and separates into its layers as the visitor scrolls, one layer highlighted at a time.
 * A coverflow slider in front shows the active layer's real screenshot; the side tabs, dots and
 * arrows are clickable and scroll the page to that layer so scroll and clicks always agree.
 * Pure CSS 3D (no WebGL bundle).
 */
const LAYERS = [
  {
    key: 'sync',
    tag: '01 · DATA',
    title: 'Accounting sync',
    text: 'Parties, products, orders and outstandings flow in from ZeroMDS or your accounting software. No re-typing.',
    color: '#6D5AD8',
    img: null,
  },
  {
    key: 'panel',
    tag: '02 · CONTROL',
    title: 'Distributor panel',
    text: 'Orders, draft orders, tickets and delivery status for your whole business on one web dashboard.',
    color: '#3462F2',
    img: '/images/logistics/distributor-dashboard.webp',
  },
  {
    key: 'map',
    tag: '03 · VISIBILITY',
    title: 'Live map',
    text: 'Every rider and salesman on the map in real time, with route timelines for each stop.',
    color: '#1FBF7A',
    img: '/images/fleet/live-locations-map.webp',
  },
  {
    key: 'app',
    tag: '04 · FIELD',
    title: 'Fleet mobile app',
    text: 'One Android app, three roles — Rider, Salesman, Employee. Tasks, attendance and orders in their pocket.',
    color: '#FFB547',
    img: '/images/mobile/salesman-dashboard.webp',
    phone: true,
  },
  {
    key: 'proof',
    tag: '05 · CLOSE',
    title: 'Delivered & collected',
    text: 'Proof of delivery, cash and cheque collections recorded at the counter and reconciled back to your books.',
    color: '#E5484D',
    img: '/images/fleet/user-activities.webp',
  },
];

const N = LAYERS.length;
const OPEN_AT = 0.15; // first 15% of the scroll opens the stack, then each layer gets an equal share

export default function PlatformReveal3D() {
  const sectionRef = useRef(null);
  const stackRef = useRef(null);
  const layerRefs = useRef([]);
  const listRef = useRef(null);
  const lockRef = useRef({ idx: -1, until: 0 });
  const reducedRef = useRef(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = sectionRef.current;
    const stack = stackRef.current;
    if (!el || !stack) return undefined;

    // Writes transforms straight to the compositor-promoted nodes; nothing re-renders per frame.
    const paint = (p) => {
      const open = Math.min(p * 1.6, 1);
      stack.style.transform = `rotateX(${20 + p * 38}deg) rotateZ(${-8 - p * 30}deg) translate3d(0, ${p * 30}px, 0)`;
      layerRefs.current.forEach((node, i) => {
        if (node) node.style.transform = `translate3d(0, 0, ${(i - 2) * (6 + open * 70)}px)`;
      });
      // Progress through the current layer drives the thin bar under the active tab.
      const pos = Math.max(0, ((p - OPEN_AT) / (1 - OPEN_AT)) * N);
      listRef.current?.style.setProperty('--lp', String(Math.min(1, pos - Math.min(N - 1, Math.floor(pos)))));
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      reducedRef.current = true;
      paint(1);
      return undefined;
    }

    let target = 0;
    let current = 0;
    let raf = 0;
    let lastIdx = -1;

    const root = document.documentElement;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      target = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 1;
      // While pinned, phones hide the back-to-top button so it doesn't sit on the slider arrows.
      root.classList.toggle('platform-pinned', rect.top <= 1 && rect.bottom >= window.innerHeight - 1);
    };

    // Eases the stack toward the scroll position so wheel steps feel smooth instead of jumpy.
    const tick = () => {
      current += (target - current) * 0.14;
      if (Math.abs(target - current) < 0.0005) current = target;
      paint(current);
      let idx = Math.min(N - 1, Math.max(0, Math.floor(((current - OPEN_AT) / (1 - OPEN_AT)) * N)));
      // After a click, hold the chosen layer while the page smooth-scrolls past the others.
      if (lockRef.current.idx >= 0) {
        if (performance.now() < lockRef.current.until && idx !== lockRef.current.idx) idx = lockRef.current.idx;
        else lockRef.current.idx = -1;
      }
      if (idx !== lastIdx) {
        lastIdx = idx;
        setActive(idx);
      }
      raf = current === target ? 0 : requestAnimationFrame(tick);
    };

    const onScroll = () => {
      measure();
      if (!raf) raf = requestAnimationFrame(tick);
    };

    // Only listen while the section is near the viewport.
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          window.addEventListener('scroll', onScroll, { passive: true });
          onScroll();
        } else {
          window.removeEventListener('scroll', onScroll);
        }
      },
      { rootMargin: '200px 0px' },
    );
    io.observe(el);
    measure();
    current = target;
    paint(current);
    window.addEventListener('resize', onScroll);

    return () => {
      root.classList.remove('platform-pinned');
      io.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  /** Click / keyboard: show layer i and scroll the page to the middle of that layer's range. */
  const goTo = (i) => {
    const idx = (i + N) % N;
    setActive(idx);
    const el = sectionRef.current;
    if (!el || reducedRef.current) return;
    const total = el.offsetHeight - window.innerHeight;
    if (total <= 0) return;
    const p = OPEN_AT + ((idx + 0.5) / N) * (1 - OPEN_AT);
    const top = el.getBoundingClientRect().top + window.scrollY + p * total;
    lockRef.current = { idx, until: performance.now() + 1100 };
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const onSliderKey = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(active + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(active - 1);
    }
  };

  const current = LAYERS[active];

  return (
    <section id="platform" ref={sectionRef} className="reveal3d" aria-labelledby="platform-title">
      <div className="reveal3d-sticky">
        <div className="fw-wrap reveal3d-grid">
          <div className="reveal3d-copy">
            <div className="fw-eyebrow">Look inside FastWhistle</div>
            <h2 id="platform-title" className="fw-h2">
              One platform. <span className="hl">Five layers</span> working for you.
            </h2>
            <p className="fw-sub" style={{ margin: '18px 0 0', maxWidth: 460 }}>
              Scroll to open it up, or tap a layer. This is what runs under every parcel we deliver — and what you get with Fleet SaaS.
            </p>
            <ol className="reveal3d-list" ref={listRef}>
              {LAYERS.map((layer, i) => (
                <li key={layer.key} className={i === active ? 'is-active' : ''} style={{ '--c': layer.color }}>
                  <button
                    type="button"
                    className="reveal3d-tab"
                    aria-current={i === active ? 'step' : undefined}
                    aria-controls="platform-slider"
                    onClick={() => goTo(i)}
                  >
                    <span className="reveal3d-dot" aria-hidden="true" />
                    <span className="reveal3d-tab-body">
                      <span className="fw-mono reveal3d-tag">{layer.tag}</span>
                      <span className="reveal3d-tab-title">{layer.title}</span>
                      <span className="reveal3d-tab-text">{layer.text}</span>
                      <span className="reveal3d-progress" aria-hidden="true" />
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </div>

          <div className="reveal3d-stage">
            {/* Decorative exploded stack behind the slider. */}
            <div className="reveal3d-stack" ref={stackRef} aria-hidden="true">
              {LAYERS.map((layer, i) => (
                <div
                  key={layer.key}
                  ref={(node) => {
                    layerRefs.current[i] = node;
                  }}
                  className={`reveal3d-layer${i === active ? ' is-active' : ''}${i > active ? ' is-above' : ''}`}
                  style={{ '--c': layer.color, zIndex: i + 1 }}
                >
                  <div className="reveal3d-slab">
                    {layer.img ? (
                      <img src={layer.img} alt="" decoding="async" draggable="false" />
                    ) : (
                      <SyncArt />
                    )}
                    <span className="reveal3d-label fw-mono">{layer.title}</span>
                  </div>
                </div>
              ))}
              <div className="reveal3d-shadow" />
            </div>

            {/* Coverflow slider with the active layer's real screen. */}
            <div
              id="platform-slider"
              className="r3-slider"
              role="region"
              aria-roledescription="carousel"
              aria-label="FastWhistle platform layers"
              tabIndex={0}
              onKeyDown={onSliderKey}
            >
              <div className="r3-track">
                {LAYERS.map((layer, i) => {
                  const off = i - active;
                  const pos = off === 0 ? 'is-active' : off === -1 ? 'is-prev' : off === 1 ? 'is-next' : off < 0 ? 'is-gone-left' : 'is-gone-right';
                  return (
                    <div
                      key={layer.key}
                      className={`r3-slide ${pos}${layer.phone ? ' is-phone' : ''}`}
                      style={{ '--c': layer.color }}
                      aria-hidden={off !== 0}
                      onClick={off !== 0 ? () => goTo(i) : undefined}
                    >
                      {layer.phone ? (
                        <div className="r3-phone">
                          <img src={layer.img} alt={`${layer.title} screen`} loading="lazy" decoding="async" draggable="false" />
                        </div>
                      ) : (
                        <div className="r3-browser">
                          <div className="browser-bar" aria-hidden="true">
                            <span /> <span /> <span />
                            <em className="fw-mono">{layer.img ? 'panel.fastwhistle.com' : 'sync.fastwhistle.com'}</em>
                          </div>
                          <div className="r3-browser-body">
                            {layer.img ? (
                              <img src={layer.img} alt={`${layer.title} screen`} loading="lazy" decoding="async" draggable="false" />
                            ) : (
                              <SyncArt />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="r3-controls">
                <button type="button" className="r3-arrow glass" aria-label="Previous layer" onClick={() => goTo(active - 1)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
                </button>
                <div className="r3-caption" aria-live="polite">
                  <span className="fw-mono" style={{ color: current.color }}>{current.tag}</span>
                  <strong>{current.title}</strong>
                </div>
                <button type="button" className="r3-arrow glass" aria-label="Next layer" onClick={() => goTo(active + 1)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
                </button>
              </div>
              <div className="r3-dots" role="tablist" aria-label="Choose a layer">
                {LAYERS.map((layer, i) => (
                  <button
                    key={layer.key}
                    type="button"
                    role="tab"
                    aria-selected={i === active}
                    aria-label={layer.title}
                    className={i === active ? 'is-active' : ''}
                    style={{ '--c': layer.color }}
                    onClick={() => goTo(i)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SyncArt() {
  const rows = ['Parties', 'Products', 'Orders', 'Outstanding'];
  return (
    <div className="reveal3d-sync">
      <div className="reveal3d-sync-box fw-display">ZeroMDS</div>
      <div className="reveal3d-sync-rows">
        {rows.map((r) => (
          <span key={r}>{r} ⇄</span>
        ))}
      </div>
      <div className="reveal3d-sync-box fw-display" style={{ background: '#3462F2' }}>FastWhistle</div>
    </div>
  );
}
